import { useEffect } from 'react';
import { BrandMark } from '../components/BrandMark';
import { IntegrationsStrip } from '../components/IntegrationsStrip';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Blocks,
  Boxes,
  GitBranch,
  BarChart3,
  Store,
  ShieldCheck,
  Plus,
  Workflow,
  Zap,
  Sparkles,
} from 'lucide-react';
import '../styles/landing.css';

/**
 * Public marketing landing page (shown at "/" to logged-out visitors).
 * "Operations blueprint" aesthetic — warm paper, ink type, emerald signal
 * accent, dot-grid, and an animated agent mesh. See styles/landing.css.
 */
export default function Landing() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'AgentMesh — Build an AI agent that answers support tickets';
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="lp min-h-screen">
      <Nav />
      <Hero />
      <TrustStrip />
      <ProblemOutcome />
      <HowItWorks />
      <SeeItWork />
      <IntegrationsStrip />
      <Features />
      <Industries />
      <Pricing />
      <Security />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}

// ── Nav ────────────────────────────────────────────────────────

function Nav() {
  return (
    <header className="sticky top-0 z-50 border-b border-[var(--line)] bg-[var(--paper)]/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
        <a href="#top" className="flex items-center gap-2">
          <MeshMark className="h-7 w-7" />
          <span className="lp-display text-lg">AgentMesh</span>
        </a>
        <nav className="hidden items-center gap-7 md:flex">
          {[
            ['How it works', '#how'],
            ['Features', '#features'],
            ['Industries', '#industries'],
            ['Pricing', '#pricing'],
            ['FAQ', '#faq'],
          ].map(([label, href]) => (
            <a
              key={href}
              href={href}
              className="text-sm text-[var(--ink-soft)] transition-colors hover:text-[var(--ink)]"
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="lp-btn lp-btn-ghost hidden sm:inline-flex">
            Log in
          </Link>
          <Link to="/register" className="lp-btn lp-btn-primary">
            Start free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </header>
  );
}

// ── Hero ───────────────────────────────────────────────────────

function Hero() {
  return (
    <section id="top" className="lp-hero">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:py-28">
        <div>
          <span className="lp-eyebrow lp-reveal" style={{ animationDelay: '40ms' }}>
            For the people who build automations for clients
          </span>
          <h1
            className="lp-display lp-reveal mt-5 text-[2.7rem] leading-[1.03] sm:text-6xl"
            style={{ animationDelay: '120ms' }}
          >
            {/* One job, and the reader is the person who builds it for someone
                else. Changed 10 Sept 2026 after five independent signals pointed
                at support — three of the four accounts built a CUSTOMER_SUPPORT
                agent unguided, the demo agent is one, Google's classifier chose
                Customer Service & Support Software, Google's own scan of THIS
                page produced "ai customer support" first of 25 and zero phrases
                about teams, and a stranger asked publicly which painful job to
                hire it for first.
                The team feature stays. It stops being the headline.
                ⚠️ Keep in step with the fallback block in index.html. */}
            Build a{' '}
            <span className="relative whitespace-nowrap">
              support agent
              <Underline />
            </span>{' '}
            that answers routine tickets.
          </h1>
          <p
            className="lp-reveal mt-6 max-w-xl text-lg text-[var(--ink-soft)]"
            style={{ animationDelay: '200ms' }}
          >
            No code, no LangChain. It escalates the ones that need a person, and
            every run shows what it did — every step, every token, what it cost.
          </p>
          <div
            className="lp-reveal mt-8 flex flex-wrap items-center gap-3"
            style={{ animationDelay: '280ms' }}
          >
            <Link to="/register" className="lp-btn lp-btn-primary text-base">
              Start free <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#how" className="lp-btn lp-btn-ghost text-base">
              See how it works
            </a>
          </div>
          <p
            className="lp-mono lp-reveal mt-5 text-xs text-[var(--ink-soft)]"
            style={{ animationDelay: '340ms' }}
          >
            Free plan · no card required · Pro is $29/mo
          </p>
          {/* /example was linked from nowhere on the site until Aug 18 2026 — a true
              orphan, reachable only via sitemap.xml and llms.txt. It is the page all
              three audit models rated strongest, and the reason the E-E-A-T
              "Experience" dimension reads Strong. The numbers below are read off that
              page (verified 2026-08-18): a support ticket triaged and answered in 8.4s
              for $0.0144 total. ⚠️ That run is a WORKED EXAMPLE (representative tokens,
              real price list — see exampleRun.ts), not a logged execution: never call
              it "real" or "measured". The one logged run on /example is $0.0204. A shop owner can multiply 1.4 cents by their ticket
              volume; "no code, no engineers" is a claim they have heard from ten
              vendors this year. Keep these in step with src/data/exampleRun.ts. */}
          <p
            className="lp-mono lp-reveal mt-2 text-xs text-[var(--ink-soft)]"
            style={{ animationDelay: '380ms' }}
          >
            <a href="/example" className="underline underline-offset-2">
              See example runs →
            </a>{' '}
            every step, every token, the cost. A worked support-ticket example,
            priced at our real rates: 8.4 seconds, $0.0144.
          </p>
        </div>

        <div className="lp-reveal" style={{ animationDelay: '260ms' }}>
          <AgentMesh />
        </div>
      </div>
    </section>
  );
}

function Underline() {
  return (
    <svg
      className="absolute -bottom-2 left-0 h-3 w-full"
      viewBox="0 0 200 12"
      fill="none"
      preserveAspectRatio="none"
      aria-hidden
    >
      <path
        d="M2 8C40 3 160 3 198 7"
        stroke="var(--lime)"
        strokeWidth="5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** The animated hero graphic: four specialist agents coordinated by an
 *  orchestrator, with live dashed connectors — a literal picture of the product. */
function AgentMesh() {
  const orch = { x: 250, y: 195 };
  const nodes = [
    { x: 250, y: 40, label: 'Customer' },
    { x: 440, y: 150, label: 'Inventory' },
    { x: 400, y: 340, label: 'Scheduling' },
    { x: 90, y: 320, label: 'Finance' },
    { x: 55, y: 120, label: 'Support' },
  ];
  return (
    <div className="lp-card lp-grid relative mx-auto max-w-[520px] p-4">
      <div className="mb-2 flex items-center justify-between px-2">
        <span className="lp-mono text-[11px] text-[var(--ink-soft)]">
          workflow · retail-ops
        </span>
        <span className="lp-eyebrow text-[10px]">live</span>
      </div>
      <svg viewBox="0 0 500 400" className="w-full" role="img"
        aria-label="Diagram of AI agents coordinated by an orchestrator">
        {nodes.map((n, i) => (
          <line
            key={i}
            x1={orch.x}
            y1={orch.y}
            x2={n.x}
            y2={n.y}
            className={`lp-mesh-line ${i < 3 ? 'is-live' : ''}`}
            style={{ animationDelay: `${i * 0.2}s` }}
          />
        ))}

        {nodes.map((n, i) => (
          <g key={n.label}>
            <rect
              x={n.x - 58}
              y={n.y - 18}
              width="116"
              height="36"
              rx="9"
              className="lp-node"
            />
            <circle
              cx={n.x - 44}
              cy={n.y}
              r="4"
              fill="var(--signal-bright)"
              className="lp-node-dot"
              style={{ animationDelay: `${i * 0.4}s` }}
            />
            <text x={n.x - 34} y={n.y + 4} className="lp-node-label">
              {n.label}
            </text>
          </g>
        ))}

        <rect
          x={orch.x - 66}
          y={orch.y - 22}
          width="132"
          height="44"
          rx="11"
          className="lp-node lp-node-orch"
        />
        <text
          x={orch.x}
          y={orch.y + 5}
          textAnchor="middle"
          className="lp-node-label lp-node-orch-label"
        >
          Orchestrator
        </text>
      </svg>
    </div>
  );
}

// ── See it work (real product footage) ─────────────────────────

/**
 * The landing page showed an illustrated diagram but never the actual product —
 * the first thing a visitor asks is "what does it look like?". This embeds the
 * real 90-second walkthrough (recorded on the live app). youtube-nocookie is
 * already allowed in the frontend CSP's frame-src.
 */
function SeeItWork() {
  return (
    <section id="see-it" className="lp-section">
      <div className="mx-auto max-w-4xl px-5">
        <div className="text-center">
          <span className="lp-eyebrow">See it work</span>
          <h2 className="lp-display mt-4 text-3xl sm:text-4xl">
            An agent built and running, in 90 seconds.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[var(--ink-soft)]">
            No mock-ups. This is the real product — install an agent, activate it, run it, and
            watch the answer come back.
          </p>
          <p className="mx-auto mt-3 max-w-xl text-sm text-[var(--ink-soft)]">
            Prefer to read it?{' '}
            <a href="/example" className="underline hover:text-[var(--ink)]">
              See a complete run — every step, token and cent, plus a real failure
            </a>
            .
          </p>
        </div>

        <div className="lp-card mt-9 overflow-hidden p-2">
          <div className="aspect-video w-full overflow-hidden rounded-lg bg-[var(--ink)]">
            <iframe
              className="h-full w-full"
              src="https://www.youtube-nocookie.com/embed/wqVhR-dIJ7g"
              title="Build your first AI agent in 90 seconds — AgentMesh"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>

        <p className="lp-mono mt-4 text-center text-xs text-[var(--ink-soft)]">
          More walkthroughs —{' '}
          <a
            href="https://www.youtube.com/@agentmesh"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[var(--ink)]"
          >
            tools · workflows · automation · marketplace
          </a>
        </p>
      </div>
    </section>
  );
}

// ── Trust strip ────────────────────────────────────────────────

function TrustStrip() {
  const stats = [
    ['6', 'orchestration modes', 'sequential · parallel · conditional · fan-out · DAG · loop'],
    ['10', 'agent tools', 'API · email · Slack · Discord · Telegram · Notion · Calendar'],
    ['4', 'industry setup scripts', 'retail · hospitality · services · manufacturing'],
    ['Encrypted & GDPR-aware', 'security posture', 'built in from day one'],
  ];
  return (
    <section className="border-y border-[var(--line)] bg-[var(--paper-2)]">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-px overflow-hidden px-5 py-2 md:grid-cols-4">
        {stats.map(([big, label, sub]) => (
          <div key={label} className="px-4 py-6">
            <div className="lp-display text-2xl">{big}</div>
            <div className="mt-1 text-sm font-medium">{label}</div>
            <div className="lp-mono mt-0.5 text-[11px] text-[var(--ink-soft)]">{sub}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Problem → outcome ──────────────────────────────────────────

function ProblemOutcome() {
  return (
    <section className="lp-section">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 md:grid-cols-2">
        <div>
          <span className="lp-eyebrow">The problem</span>
          <h2 className="lp-display mt-4 text-3xl sm:text-4xl">
            Your tools don&apos;t talk. Your team pays for it.
          </h2>
          <ul className="mt-6 space-y-3 text-[var(--ink-soft)]">
            {[
              'A chatbot here, a spreadsheet there, an inbox on fire.',
              'AI that needs engineers you don’t have on payroll.',
              'Data scattered across POS, CRM, calendars and accounting.',
              'Nothing coordinates — so the busywork lands on people.',
            ].map((t) => (
              <li key={t} className="lp-tick">
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="lp-card p-8">
          <span className="lp-eyebrow">The outcome</span>
          <h2 className="lp-display mt-4 text-3xl sm:text-4xl">
            One coordinated team, running quietly in the background.
          </h2>
          <ul className="mt-6 space-y-3">
            {[
              'Agents hand off to each other like real coworkers.',
              'Set it up once with drag-and-drop — no code.',
              'Watch every run in real time with full analytics.',
              'Your people do the work only people should do.',
            ].map((t) => (
              <li key={t} className="lp-tick font-medium">
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ── How it works ───────────────────────────────────────────────

function HowItWorks() {
  const steps = [
    {
      icon: Store,
      k: '01',
      title: 'Pick a template',
      body: 'Start from an industry playbook or a marketplace template — support, inventory, scheduling and more.',
    },
    {
      icon: Workflow,
      k: '02',
      title: 'Assemble your team',
      body: 'Drag agents onto a canvas and connect them: sequential, parallel, conditional, fan-out, or a dependency graph.',
    },
    {
      icon: Sparkles,
      k: '03',
      title: 'They run together',
      body: 'An orchestrator routes each task between agents and reports back — live, with logs and metrics.',
    },
  ];
  return (
    <section id="how" className="lp-section lp-grid">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <span className="lp-eyebrow">How it works</span>
          <h2 className="lp-display mt-4 text-3xl sm:text-4xl">
            From idea to a working AI team in three steps.
          </h2>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.k} className="lp-card p-7">
              <div className="flex items-center justify-between">
                <s.icon className="h-6 w-6 text-[var(--signal)]" />
                <span className="lp-mono text-sm text-[var(--line-strong)]">{s.k}</span>
              </div>
              <h3 className="lp-display mt-5 text-xl">{s.title}</h3>
              <p className="mt-2 text-[var(--ink-soft)]">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Features ───────────────────────────────────────────────────

function Features() {
  const feats = [
    { icon: Blocks, title: 'No-code builder', body: 'A visual canvas for agents and logic. If you can sketch a flow, you can build one.' },
    { icon: GitBranch, title: 'Real orchestration', body: 'Sequential, parallel, conditional, fan-out and dependency graphs — with structured hand-offs between agents.' },
    { icon: Zap, title: 'Agents that act & automate', body: 'Agents call your APIs, send email & Slack, fire webhooks and update Google Calendar — and run on a schedule or when an event hits.' },
    { icon: Boxes, title: 'Industry templates', body: 'Prebuilt agent teams for retail, hospitality, services and manufacturing.' },
    { icon: BarChart3, title: 'Real-time analytics', body: 'Every execution tracked — latency, cost, success rate — with live event streams.' },
    { icon: Store, title: 'Marketplace', body: 'Install community and official templates in a click, or publish your own.' },
    { icon: ShieldCheck, title: 'Security first', body: 'Tenant isolation, JWT auth, rate limiting, encrypted secrets, and a GDPR-aware posture.' },
  ];
  return (
    <section id="features" className="lp-section">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <span className="lp-eyebrow">Features</span>
          <h2 className="lp-display mt-4 text-3xl sm:text-4xl">
            Everything to run agents in production — not just a demo.
          </h2>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {feats.map((f) => (
            <div key={f.title} className="lp-card p-7">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[var(--paper-2)]">
                <f.icon className="h-5 w-5 text-[var(--signal)]" />
              </div>
              <h3 className="lp-display mt-5 text-lg">{f.title}</h3>
              <p className="mt-2 text-sm text-[var(--ink-soft)]">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Industries ─────────────────────────────────────────────────

function Industries() {
  const items = [
    'Retail', 'Hospitality', 'Professional Services', 'Manufacturing',
    'E-commerce', 'Field Services', 'Agencies',
  ];
  const row = [...items, ...items];
  return (
    <section id="industries" className="lp-section lp-grid overflow-hidden">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <span className="lp-eyebrow">Built for operators</span>
          <h2 className="lp-display mt-4 text-3xl sm:text-4xl">
            Playbooks for the businesses that keep the lights on.
          </h2>
        </div>
      </div>
      <div className="lp-marquee-wrap mt-10 [mask-image:linear-gradient(90deg,transparent,#000_8%,#000_92%,transparent)]">
        <div className="lp-marquee">
          {row.map((label, i) => (
            <span
              key={i}
              className="lp-display whitespace-nowrap text-2xl text-[var(--ink-soft)]"
            >
              {label}
              <span className="mx-6 text-[var(--signal)]">✳</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Pricing ────────────────────────────────────────────────────

function Pricing() {
  return (
    <section id="pricing" className="lp-section">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <span className="lp-eyebrow">Pricing</span>
          <h2 className="lp-display mt-4 text-3xl sm:text-4xl">
            Start free. Upgrade when it&apos;s paying for itself.
          </h2>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 md:max-w-3xl">
          <div className="lp-card flex flex-col p-8">
            <div className="lp-mono text-sm text-[var(--ink-soft)]">FREE</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="lp-display text-5xl">$0</span>
              <span className="text-[var(--ink-soft)]">/forever</span>
            </div>
            <p className="mt-3 text-sm text-[var(--ink-soft)]">
              Everything you need to build your first AI team.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {['Build agents & workflows', 'Community marketplace templates', 'Real-time execution logs', 'Core analytics'].map((t) => (
                <li key={t} className="lp-tick">{t}</li>
              ))}
            </ul>
            <Link to="/register" className="lp-btn lp-btn-ghost mt-8 w-full">
              Start free
            </Link>
          </div>

          <div className="lp-card relative flex flex-col border-[var(--signal)] p-8 shadow-[0_24px_60px_-30px_rgba(5,136,95,0.55)]">
            <div className="lp-mono absolute right-5 top-6 rounded-full bg-[var(--ink)] px-2.5 py-1 text-[10px] text-[var(--paper)]">
              FOR DAILY USE
            </div>
            <div className="lp-mono text-sm text-[var(--signal)]">PRO</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="lp-display text-5xl">$29</span>
              <span className="text-[var(--ink-soft)]">/month</span>
            </div>
            <p className="mt-3 text-sm text-[var(--ink-soft)]">
              For teams running agents every day.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {['Everything in Free', 'All orchestration modes', 'Full marketplace + publishing', 'Advanced cost & performance analytics', 'Priority support'].map((t) => (
                <li key={t} className="lp-tick font-medium">{t}</li>
              ))}
            </ul>
            <Link to="/register" className="lp-btn lp-btn-primary mt-8 w-full">
              Start free <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="lp-mono mt-3 text-center text-[11px] text-[var(--ink-soft)]">
              Upgrade to Pro any time from your dashboard
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Security ───────────────────────────────────────────────────

function Security() {
  return (
    <section className="lp-section bg-[var(--ink)] text-[var(--paper)]">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 md:grid-cols-[1fr_1.1fr]">
        <div>
          <span className="lp-eyebrow" style={{ color: 'var(--lime)' }}>
            Security &amp; compliance
          </span>
          <h2 className="lp-display mt-4 text-3xl sm:text-4xl">
            Guardrails that are on by default.
          </h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            ['Workspace isolation', 'Multi-tenant by design — your data stays yours.'],
            ['Encrypted & GDPR-aware', 'Encryption at rest, tenant isolation, and your keys under your control.'],
            ['Signed auth & rate limits', 'JWT sessions, throttled endpoints, audit trails.'],
            ['Your keys, your control', 'Bring your own LLM providers and rotate any time.'],
          ].map(([t, b]) => (
            <div key={t} className="rounded-xl border border-white/12 bg-white/[0.04] p-5">
              <ShieldCheck className="h-5 w-5" style={{ color: 'var(--lime)' }} />
              <h3 className="lp-display mt-3 text-lg">{t}</h3>
              <p className="mt-1 text-sm text-white/65">{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── FAQ ────────────────────────────────────────────────────────

function Faq() {
  const qas = [
    ['Do I need to know how to code?', 'No. AgentMesh is a visual, drag-and-drop builder. If you can describe how a task should flow, you can build it.'],
    ['What can the agents actually do?', 'Handle customer questions, check inventory, schedule work, draft invoices, process data — and hand off to each other through an orchestrator.'],
    ['How does the Free plan work?', 'Free forever, no card required. Build agents and workflows, use community templates, and see live logs. Upgrade to Pro ($29/mo) for all orchestration modes and advanced analytics.'],
    ['Is my data secure?', 'The security section above covers how — isolation, encryption, signed auth, your own keys. The part most vendors bury: we are GDPR-aware but NOT SOC 2 or HIPAA certified, and we say so rather than implying otherwise.'],
    ['Can I publish my own templates?', 'On Pro, yes. Submit a template and, once approved, it’s installable from the marketplace by other teams.'],
  ];
  return (
    <section id="faq" className="lp-section">
      <div className="mx-auto max-w-3xl px-5">
        <span className="lp-eyebrow">FAQ</span>
        <h2 className="lp-display mt-4 text-3xl sm:text-4xl">Questions, answered.</h2>
        <div className="lp-faq mt-8 divide-y divide-[var(--line)] border-y border-[var(--line)]">
          {qas.map(([q, a]) => (
            <details key={q} className="group py-4">
              <summary className="flex items-center justify-between gap-4">
                <span className="lp-display text-lg">{q}</span>
                <Plus className="lp-faq-plus h-5 w-5 shrink-0 text-[var(--signal)]" />
              </summary>
              <p className="mt-3 text-[var(--ink-soft)]">{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Final CTA ──────────────────────────────────────────────────

function FinalCta() {
  return (
    <section className="lp-section lp-hero">
      <div className="mx-auto max-w-4xl px-5 text-center">
        <MeshMark className="mx-auto h-10 w-10" />
        <h2 className="lp-display mt-6 text-4xl sm:text-5xl">
          Run receipts for every client job.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-lg text-[var(--ink-soft)]">
          Build your first AI team today. Free to start, live in minutes.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/register" className="lp-btn lp-btn-primary text-base">
            Start free <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/contact" className="lp-btn lp-btn-ghost text-base">
            Talk to us
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Footer ─────────────────────────────────────────────────────

/**
 * Paths that exist ONLY as prerendered HTML, with no route in App.tsx. Linking
 * to one with react-router's <Link> renders NotFound, because the router owns
 * the click and has nothing to match. Keep in step with the pages written by
 * scripts/dev/gen-content-pages.mjs that have no React twin.
 */
const PRERENDER_ONLY = new Set(['/agent-library', '/consultants']);

function Footer() {
  // Grouped into four balanced columns. It was three, with ten items stacked
  // under Product against two columns of three — a tall thin list beside a lot
  // of empty space, and no signal about which links are proof and which are
  // product. "Learn" is the evidence set: the pages a stranger reads before
  // deciding, all of which exist because they were once unreachable.
  //
  // ⚠️ 'Agent library' pointed at /agents until 12 Sept 2026 — the app's own
  // protected route — so the footer link redirected to /login while the page
  // itself was fine at /agent-library. Any path in PRERENDER_ONLY must match
  // the set exactly; the build guard compares them.
  const cols = [
    ['Product', [
      ['How it works', '#how'],
      ['Features', '#features'],
      ['Pricing', '#pricing'],
      ['Compare', '/compare'],
      ['Industries', '/solutions'],
    ]],
    ['Learn', [
      ['See a real run', '/example'],
      ['Agent library', '/agent-library'],
      ['For consultants', '/consultants'],
      ['Writing', '/blog'],
      ['Help centre', '/help'],
    ]],
    ['Company', [
      ['Contact', '/contact'],
      ['Log in', '/login'],
      ['Start free', '/register'],
    ]],
    ['Legal', [
      ['Terms', '/terms'],
      ['Privacy', '/privacy'],
      ['Refund policy', '/refund'],
    ]],
  ] as const;
  return (
    <footer className="border-t border-[var(--line)] bg-[var(--paper-2)]">
      <div className="mx-auto max-w-6xl px-5 py-14">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-[1.6fr_repeat(4,minmax(0,1fr))] lg:gap-8">
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2">
              <MeshMark className="h-7 w-7" />
              <span className="lp-display text-lg">AgentMesh</span>
            </div>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-[var(--ink-soft)]">
              AI agents that do one job each, with a receipt for every run: each step, its tokens and its cost.
            </p>
            {/* The one link a stranger should click before anything else. It is
                the page two people asked for by name, and it needs no account. */}
            <a
              href="/example"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--ink)] underline decoration-[var(--line)] decoration-2 underline-offset-4 transition-colors hover:decoration-[var(--ink)]"
            >
              See a real run
              <span aria-hidden="true">→</span>
            </a>
          </div>

          {cols.map(([title, links]) => (
            <nav key={title} aria-label={title}>
              <h2 className="lp-mono text-[0.7rem] uppercase tracking-[0.14em] text-[var(--ink-soft)]">
                {title}
              </h2>
              <ul className="mt-4 space-y-2.5">
                {/* PRERENDER_ONLY paths have no React route: they exist only as
                    static HTML that nginx serves. A <Link> to one is handled by
                    react-router, finds no route and renders NotFound — which is
                    exactly what /consultants did, while curl fetched the file
                    directly and reported success. They need a real navigation. */}
                {links.map(([label, href]) => {
                  const cls =
                    'text-sm text-[var(--ink-soft)] transition-colors hover:text-[var(--ink)]';
                  return (
                    <li key={label}>
                      {href.startsWith('#') || PRERENDER_ONLY.has(href) ? (
                        <a href={href} className={cls}>
                          {label}
                        </a>
                      ) : (
                        <Link to={href} className={cls}>
                          {label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          ))}
        </div>
      </div>

      <div className="border-t border-[var(--line)]">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-6 text-xs text-[var(--ink-soft)] sm:flex-row">
          <span className="lp-mono">© 2026 AgentMesh. All rights reserved.</span>
          {/* Said on the /about page too. It is a differentiator rather than a
              disclaimer: the honest scale is part of the pitch. */}
          <span className="text-center sm:text-right">
            Built and run by{' '}
            <Link to="/about" className="underline underline-offset-2 transition-colors hover:text-[var(--ink)]">
              one engineer
            </Link>
            , not a venture-backed team.
          </span>
        </div>
      </div>
    </footer>
  );
}

// ── Brand mark: a small agent mesh ─────────────────────────────

function MeshMark({ className }: { className?: string }) {
  // The AgentMesh brand mark (Node-A) — adapts to the surrounding text color.
  return <BrandMark className={className} />;
}
